import { z } from "zod";

/**
 * Per-org report settings are deliberately minimal: staleness thresholds (each
 * org's definition of "too long"), the sales-rep follow-ups toggle, and nothing
 * else. Everything structural — status sets, sorting, row caps, aging anchors —
 * is a code default in the template builders, added back as a setting only when
 * an org actually asks to differ.
 *
 * Every day threshold here counts business days (Mon–Fri, no holiday calendar)
 * in the org's timezone, so the settings UI and the report legends must say so.
 */

/**
 * Standard template settings — the pending-tasks report. Deliberately empty:
 * Standard is the org-wide default and fully code-defined; its org context
 * blurb lives on the template row, not in the bag.
 */
export const StandardSettingsSchema = z.object({});
export type StandardSettings = z.infer<typeof StandardSettingsSchema>;

/** Sales Rep template settings. */
export const SalesRepSettingsSchema = z.object({
  /** Shows the customer follow-ups section. */
  followUpsEnabled: z.boolean().default(true),
  /**
   * Business days after a quote is sent on which the rep is nudged to follow up.
   * This is the rep's own reminder cadence, deliberately independent of the
   * standard agent's follow-up intervals (those govern whether the *agent*
   * chases the customer, which is a different decision).
   */
  followUpDays: z
    .array(z.number().int().positive())
    .default([2, 5, 10])
    .transform((days) => [...new Set(days)].sort((a, b) => a - b)),
  inFlightStalenessDays: z.number().int().nonnegative().default(1),
});
export type SalesRepSettings = z.infer<typeof SalesRepSettingsSchema>;

/** Estimator template settings. */
export const EstimatorSettingsSchema = z.object({
  blockedTaskMinDays: z.number().int().nonnegative().default(1),
});
export type EstimatorSettings = z.infer<typeof EstimatorSettingsSchema>;

/** Manager template settings — the deal-acceleration report. */
export const ManagerSettingsSchema = z.object({
  stuckDays: z.number().int().positive().default(10),
  sentNoReplyDays: z.number().int().positive().default(10),
});
export type ManagerSettings = z.infer<typeof ManagerSettingsSchema>;

/**
 * Parses a raw settings bag into typed, fully-defaulted Standard settings. These
 * per-template functions are the one boundary where persisted jsonb becomes typed
 * settings; an empty bag yields every default, and unknown (removed) keys are
 * ignored, so slimming the schema never breaks stored bags.
 */
export function parseStandardSettings(raw: unknown): StandardSettings {
  return StandardSettingsSchema.parse(raw ?? {});
}

/** Parses a raw settings bag into typed, fully-defaulted Sales Rep settings. */
export function parseSalesRepSettings(raw: unknown): SalesRepSettings {
  return SalesRepSettingsSchema.parse(raw ?? {});
}

/** Parses a raw settings bag into typed, fully-defaulted Estimator settings. */
export function parseEstimatorSettings(raw: unknown): EstimatorSettings {
  return EstimatorSettingsSchema.parse(raw ?? {});
}

/** Parses a raw settings bag into typed, fully-defaulted Manager settings. */
export function parseManagerSettings(raw: unknown): ManagerSettings {
  return ManagerSettingsSchema.parse(raw ?? {});
}
